import React, { useCallback, useEffect, useRef, useState } from 'react';
import { api, getLastEmail, rememberEmail, setSession } from '../lib/api';
import { GOOGLE_CLIENT_ID } from '../lib/config';

/* The door. Two ways in — Google, or an email and password for anyone who'd
   rather not use Google — and an invite decides whether someone new can join.

   Members just sign in and land on their own profile — no codes. Someone new
   needs an invite: it arrives as a link with the code already in it, and is
   used up when they join. */

const GSI_SRC = 'https://accounts.google.com/gsi/client';

function loadGoogle() {
  if (window.google?.accounts?.id) return Promise.resolve();
  return new Promise((resolve, reject) => {
    const existing = document.querySelector(`script[src="${GSI_SRC}"]`);
    const script = existing || document.createElement('script');
    script.addEventListener('load', () => resolve());
    script.addEventListener('error', () => reject(new Error('Google sign-in could not load.')));
    if (!existing) {
      script.src = GSI_SRC;
      script.async = true;
      document.head.appendChild(script);
    }
  });
}

export default function Enter({ onEntered, embedded = false, joinKey = 0 }) {
  const [invite, setInvite] = useState('');
  // Came in through an invite link: the code is already here, so don't make them look at it.
  const [fromLink, setFromLink] = useState(false);
  const [inviteInfo, setInviteInfo] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [googleReady, setGoogleReady] = useState(false);
  const [mode, setMode] = useState('signin'); // email form: 'signin' | 'join'
  // The landing page's "I have an invite code" switches this card to joining.
  useEffect(() => { if (joinKey) setMode('join'); }, [joinKey]);
  const [name, setName] = useState('');
  const [email, setEmail] = useState(() => getLastEmail() || '');
  const [password, setPassword] = useState('');
  const button = useRef(null);
  // The Google callback is registered once; it must read the code as it is now.
  const inviteRef = useRef('');
  useEffect(() => { inviteRef.current = invite; }, [invite]);

  useEffect(() => {
    try {
      const fromLink = new URLSearchParams(window.location.search).get('invite');
      if (fromLink) {
        setInvite(fromLink.trim().toUpperCase());
        setFromLink(true);
        setMode('join');
      }
    } catch { /* no URL API */ }
    try {
      const n = window.sessionStorage.getItem('bn.notice');
      if (n) {
        setNotice(n);
        window.sessionStorage.removeItem('bn.notice');
      }
    } catch { /* private mode */ }
  }, []);

  // Tell an invited person whose invite it is, and whether it's still good.
  useEffect(() => {
    const code = invite.trim();
    if (code.length < 6) { setInviteInfo(null); return undefined; }
    const t = setTimeout(() => {
      api.checkInvite(code).then(setInviteInfo).catch(() => setInviteInfo(null));
    }, 300);
    return () => clearTimeout(t);
  }, [invite]);

  const finish = useCallback((session) => {
    setSession(session);
    try {
      window.history.replaceState({}, '', window.location.pathname);
    } catch { /* old browsers */ }
    onEntered(session);
  }, [onEntered]);

  const onCredential = useCallback(async (response) => {
    setBusy(true);
    setError('');
    try {
      finish(await api.googleSignIn(response.credential, inviteRef.current.trim()));
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  }, [finish]);

  async function submitEmail(e) {
    e.preventDefault();
    setBusy(true);
    setError('');
    try {
      const addr = email.trim();
      const session = mode === 'join'
        ? await api.signup({ invite_code: invite.trim(), email: addr, password, name: name.trim() })
        : await api.login(addr, password);
      rememberEmail(addr);
      finish(session);
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  }

  const joining = mode === 'join';
  const canSubmit = email.trim() && password.length >= (joining ? 10 : 1)
    && (!joining || (name.trim().length >= 2 && invite.trim()));

  useEffect(() => {
    let cancelled = false;
    loadGoogle()
      .then(() => {
        if (cancelled || !button.current) return;
        window.google.accounts.id.initialize({
          client_id: GOOGLE_CLIENT_ID,
          callback: onCredential,
          ux_mode: 'popup',
          auto_select: false,
          cancel_on_tap_outside: true,
        });
        window.google.accounts.id.renderButton(button.current, {
          type: 'standard', theme: 'outline', size: 'large', text: 'continue_with',
          shape: 'pill', logo_alignment: 'left', width: 300,
        });
        setGoogleReady(true);
      })
      .catch((e) => setError(`${e.message} Check your connection, or allow accounts.google.com.`));
    return () => { cancelled = true; };
  }, [onCredential]);

  return (
    <div className={embedded ? 'enter-embedded' : 'enter-wrap'}>
      <div className={embedded ? 'enter-card landing-signin' : 'enter-card'} id="sign-in">
        {embedded ? (
          <>
            <h2 className="landing-signin-h">{mode === 'join' ? 'Join with your invite' : 'Welcome back, builder'}</h2>
            <p className="muted-sm landing-signin-sub">Sign in, or join with your invite.</p>
          </>
        ) : (
          <>
            <img className="enter-logo" src={`${process.env.PUBLIC_URL}/icon.svg`} alt="Builders' Network" />
            <h1>Builders' Network</h1>
          </>
        )}

        {inviteInfo?.valid && (
          <p className="enter-invited">
            {inviteInfo.inviter ? <><b>{inviteInfo.inviter}</b> invited you. </> : "You've been invited. "}
            {inviteInfo.credit_usd > 0 && <>It includes <b>${inviteInfo.credit_usd.toFixed(0)}</b> of Copilot credit. </>}
            Continue with Google, or join with your email below.
          </p>
        )}
        {inviteInfo && !inviteInfo.valid && <div className="enter-error">{inviteInfo.reason}</div>}

        {!embedded && (
          <p className="enter-lede">
            A private network for people building in Web3, AI and fintech. New members need an
            invite.
          </p>
        )}

        {notice && <div className="enter-notice">{notice}</div>}

        <div className="enter-google">
          <div ref={button} aria-busy={busy} />
          {!googleReady && !error && <p className="muted-sm">Loading Google sign-in…</p>}
          {busy && <p className="muted-sm">Signing you in…</p>}
        </div>

        {error && <div className="enter-error">{error}</div>}

        <div className="enter-or"><span>or use your email</span></div>

        <div className="enter-tabs" role="tablist">
          <button type="button" role="tab" aria-selected={!joining}
            className={!joining ? 'on' : ''} onClick={() => setMode('signin')}>Sign in</button>
          <button type="button" role="tab" aria-selected={joining}
            className={joining ? 'on' : ''} onClick={() => setMode('join')}>Join with an invite</button>
        </div>

        <form className="enter-form" onSubmit={submitEmail}>
          {joining && (
            <>
              {fromLink && inviteInfo?.valid ? (
                <p className="enter-code-ok">
                  Invite <code>{invite}</code> ✓{' '}
                  <button type="button" className="linklike" onClick={() => setFromLink(false)}>use a different code</button>
                </p>
              ) : (
                <>
                  <label className="flabel" htmlFor="invite">Invite code</label>
                  <input
                    id="invite" className="in" value={invite} placeholder="Paste the code from your invite"
                    onChange={(e) => setInvite(e.target.value.toUpperCase())}
                    autoComplete="off" spellCheck={false}
                  />
                  <p className="muted-sm enter-code-hint">It looks like BN-7KQ2-MD and is in the invite link or email a member sent you.</p>
                </>
              )}
              <label className="flabel" htmlFor="name">Your name</label>
              <input id="name" className="in" value={name} autoComplete="name"
                onChange={(e) => setName(e.target.value)} />
            </>
          )}
          <label className="flabel" htmlFor="email">Email</label>
          <input id="email" className="in" type="email" value={email} autoComplete="email"
            onChange={(e) => setEmail(e.target.value)} />
          <label className="flabel" htmlFor="password">Password</label>
          <input id="password" className="in" type="password" value={password}
            autoComplete={joining ? 'new-password' : 'current-password'}
            onChange={(e) => setPassword(e.target.value)} />
          {joining && <p className="muted-sm">At least 10 characters.</p>}
          {!joining && (
            <p className="muted-sm">Forgot it, or never set one? Ask an admin for a temporary password.</p>
          )}
          <button className="btn primary" type="submit" disabled={busy || !canSubmit}>
            {busy ? 'One moment…' : joining ? 'Join' : 'Sign in'}
          </button>
        </form>

        <p className="enter-consent">
          By continuing you agree to our <a href="/terms" target="_blank" rel="noreferrer">Terms</a> and{' '}
          <a href="/privacy" target="_blank" rel="noreferrer">Privacy Policy</a>.
        </p>

        {!embedded && <p className="enter-note">
          🔒 Only signed-in members can see your profile. What you're seeking, your files and
          your Copilot chats stay private unless you choose to share them. If you use Google, we
          only use it to confirm it's you — never to post or read your email.
        </p>}
      </div>
    </div>
  );
}
